import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import PLAN_URL from "../../../utils/PLAN_URL";
import { MaterialReactTable } from "material-react-table";
import {
  Button,
  Typography,
  CircularProgress,
  Paper,
  Box,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  MenuItem,
  ListItemText,
  OutlinedInput,
  Skeleton,
  Fade,
  Chip,
  TextField,
  InputAdornment,
  Collapse,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  Tooltip,
  Drawer,
  Badge,
  Zoom,
  Slide,
  Popper,
  ClickAwayListener,
  Grow,
} from "@mui/material";
import styled from "styled-components";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import "dayjs/locale/ko";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import TableChartIcon from "@mui/icons-material/TableChart";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AssessmentIcon from "@mui/icons-material/Assessment";
import FilterListIcon from "@mui/icons-material/FilterList";
import DateRangeIcon from "@mui/icons-material/DateRange";
import SettingsIcon from "@mui/icons-material/Settings";
import CloseIcon from "@mui/icons-material/Close";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import ReactEcharts from "echarts-for-react";
import { useParams } from "react-router-dom";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

// FullCalendar 관련 임포트
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import koLocaleFC from "@fullcalendar/core/locales/ko";

// 한국어 로케일 설정
dayjs.locale("ko");

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: calc(100vh - 65px);
  background-color: var(--mui-palette-background-default);
  overflow: hidden;
  box-sizing: border-box;
`;

const FrameContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  flex: 1;
  overflow: hidden;
  background-color: var(--mui-palette-background-paper);
  box-shadow: var(--mui-shadows-2);
`;

const ToolbarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid var(--mui-palette-divider);
  z-index: 10;
`;

const ToolbarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const ToolbarRight = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100% - 65px);
  overflow: hidden;
  position: relative;
`;

const MainContent = styled.div`
  flex: 1;
  height: 100%;
  overflow-y: auto;
  padding: 16px;
  z-index: 1;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(99, 96, 150, 0.7);
    border-radius: 5px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
`;

const ChartContainer = styled(Paper)`
  margin-bottom: 16px;
  padding: 16px;
  box-sizing: border-box;
  position: relative;
  height: 400px;
`;

const TableContainer = styled(Paper)`
  margin-bottom: 16px;
`;

const CalendarContainer = styled(Paper)`
  margin-bottom: 16px;
  padding: 16px;
  box-sizing: border-box;
  position: relative;
  height: calc(100vh - 200px);
  overflow: hidden;

  .fc {
    height: 100%;
  }

  .fc-event {
    cursor: pointer;
  }

  .fc-day-today {
    background-color: rgba(25, 118, 210, 0.05) !important;
  }

  .fc-daygrid-day-number {
    font-weight: 500;
  }

  .production-event {
    background-color: #1890ff;
    border-color: #1890ff;
  }

  .input-event {
    background-color: #52c41a;
    border-color: #52c41a;
  }

  .output-event {
    background-color: #fa8c16;
    border-color: #fa8c16;
  }

  .fc-h-event .fc-event-title {
    font-weight: 500;
  }

  .fc-day-sat .fc-daygrid-day-number {
    color: #0066cc;
  }

  .fc-day-sun .fc-daygrid-day-number {
    color: #e91e63;
  }
`;

const ItemSelectionPanel = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 320px;
  height: 100%;
  z-index: 10;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background-color: #fff;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
  transform: translateX(${(props) => (props.$isOpen ? "0" : "100%")});
  transition: transform 0.3s ease-in-out;
`;

const DateSelectionPanel = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  transform: translateX(-50%)
    translateY(${(props) => (props.$isOpen ? "0" : "-103%")});
  width: 420px;
  z-index: 10;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background-color: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-radius: 0 0 8px 8px;
  transition: transform 0.3s ease-in-out;
`;

const PanelHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid var(--mui-palette-divider);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #f5f5f5;
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(99, 96, 150, 0.7);
    border-radius: 5px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
`;

const CategoryContainer = styled(Box)`
  margin-bottom: 12px;
`;

const CategoryHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: 4px;
  background-color: rgba(0, 0, 0, 0.03);
  cursor: pointer;
  &:hover {
    background-color: rgba(0, 0, 0, 0.06);
  }
`;

const ItemList = styled(Box)`
  padding: 6px 12px 6px 24px;
`;

const SelectedItemsContainer = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 16px;
  border-radius: 4px;
  border: 1px solid var(--mui-palette-divider);
  background-color: rgba(0, 0, 0, 0.02);
  min-height: 48px;
  margin-bottom: 16px;
`;

const StyledChip = styled(Chip)`
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const ControlsContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 16px;
`;

const DateRangeContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LegendContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 8px;
`;

const LegendItem = styled(Box)`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const LegendColor = styled(Box)`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background-color: ${(props) => props.color};
`;

const SelectedItemsPreview = styled(Box)`
  display: flex;
  align-items: center;
  gap: 4px;
  border-radius: 16px;
  padding: 4px 8px 4px 12px;
  background-color: #1976d2;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  &:hover {
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  }
`;

const ToolbarIconButton = styled(IconButton)`
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: -2px;
    left: 50%;
    transform: translateX(-50%) scaleX(${(props) => (props.$active ? 1 : 0)});
    height: 2px;
    width: 70%;
    background-color: #1976d2;
    transition: transform 0.2s ease;
    border-radius: 1px;
  }
`;

const DateRangeDisplay = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px 12px;
  border-radius: 16px;
  background-color: #7c78bf;
  color: #fff;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  &:hover {
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  }
`;

const DatePickerContainer = styled(Box)`
  padding: 8px 16px 16px;
  border-radius: 8px;
  background-color: #fff;
`;

const PresetButtonGroup = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;

const PresetButton = styled(Button)`
  flex: 1;
  min-width: 90px;
`;

const DatePickerWrapper = styled(Box)`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
`;

const ButtonGroup = styled(Box)`
  display: flex;
  gap: 8px;
  margin-top: 16px;
`;

function ResultGrid() {
  const { group_code, project_code } = useParams();
  const theme = useTheme();
  const [summaryData, setSummaryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState(["A1", "B2", "C1"]);
  const [chartInstance, setChartInstance] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedCategories, setExpandedCategories] = useState({
    원자재: true,
    부품: false,
    완제품: false,
    포장재: false,
  });
  const [viewMode, setViewMode] = useState("table");
  const [dateRange, setDateRange] = useState({
    start: dayjs("2025-09-01"),
    end: dayjs("2025-09-21"),
  });
  // 날짜 선택 시 임시 저장할 상태 추가
  const [tempDateRange, setTempDateRange] = useState({
    start: dayjs("2025-09-01"),
    end: dayjs("2025-09-21"),
  });
  const [presetPeriod, setPresetPeriod] = useState("custom");
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [isItemPanelOpen, setIsItemPanelOpen] = useState(false);
  const [isDatePanelOpen, setIsDatePanelOpen] = useState(false);
  const calendarRef = useRef(null);
  const mainContentRef = useRef(null);
  const chartRef = useRef(null);

  const itemCategories = useMemo(
    () => ({
      원자재: ["A1", "A2", "A3", "A4", "A5", "A6", "A7", "A8", "A9", "A10"],
      부품: ["B1", "B2", "B3", "B4", "B5", "B6", "B7", "B8", "B9", "B10"],
      완제품: ["C1", "C2", "C3", "C4", "C5", "C6", "C7", "C8", "C9", "C10"],
      포장재: ["D1", "D2", "D3", "D4", "D5", "D6", "D7", "D8", "D9", "D10"],
    }),
    []
  );
  const displayDates = useMemo(() => {
    const dates = [];
    let currentDate = dateRange.start.clone();
    const endDate = dateRange.end;

    while (
      currentDate.isBefore(endDate) ||
      currentDate.isSame(endDate, "day")
    ) {
      dates.push(currentDate.format("MM월 DD일"));
      currentDate = currentDate.add(1, "day");
    }
    return dates;
  }, [dateRange]);

  const chartDateLabels = useMemo(() => {
    return displayDates.map((date) => date.replace("월 ", "/"));
  }, [displayDates]);
  const itemDetails = useMemo(() => {
    const details = {};
    Object.entries(itemCategories).forEach(([category, items]) => {
      items.forEach((item) => {
        details[item] = {
          id: item,
          name: `${category} ${item}`,
          category: category,
          color: item.startsWith("A")
            ? "#1890ff"
            : item.startsWith("B")
            ? "#52c41a"
            : item.startsWith("C")
            ? "#fa8c16"
            : "#722ed1",
        };
      });
    });
    return details;
  }, [itemCategories]);

  const onChartReady = useCallback((echarts) => {
    setChartInstance(echarts);
  }, []);

  const generateHierarchicalInventoryData = useCallback(() => {
    const allItems = [];
    Object.values(itemCategories).forEach((items) => {
      allItems.push(...items);
    });
    const result = [];
    allItems.forEach((item) => {
      result.push({
        id: `item-${item}`,
        type: `아이템 ${item}`,
        isGroupHeader: true,
        itemCode: item,
        value: null,
        dates: {},
      });
      const inputData = {
        id: `item-${item}-input`,
        type: "입고량",
        parent: `item-${item}`,
        itemCode: item,
        value: null,
        dates: {},
      };
      const usageData = {
        id: `item-${item}-usage`,
        type: "사용량",
        parent: `item-${item}`,
        itemCode: item,
        value: null,
        dates: {},
      };
      const stockData = {
        id: `item-${item}-stock`,
        type: "재고량",
        parent: `item-${item}`,
        itemCode: item,
        value: Math.floor(Math.random() * 100) + 50,
        dates: {},
      };
      const safetyStockData = {
        id: `item-${item}-safety`,
        type: "안전재고",
        parent: `item-${item}`,
        itemCode: item,
        value: 30,
        dates: {},
      };
      displayDates.forEach((dateStr, index) => {
        const dayNum = parseInt(dateStr.substring(4, 6));
        if (item.startsWith("A")) {
          if (dayNum % 3 === 0)
            inputData.dates[dateStr] = Math.floor(Math.random() * 50) + 50;
          usageData.dates[dateStr] =
            dayNum % 3 === 0 ? 30 : dayNum % 3 === 1 ? 20 : 10;
        } else if (item.startsWith("B")) {
          if (dayNum % 4 === 0)
            inputData.dates[dateStr] = Math.floor(Math.random() * 30) + 40;
          usageData.dates[dateStr] =
            dayNum % 4 === 0
              ? 40
              : dayNum % 4 === 1
              ? 30
              : dayNum % 4 === 2
              ? 20
              : 10;
        } else if (item.startsWith("C")) {
          if (dayNum % 5 === 0)
            inputData.dates[dateStr] = Math.floor(Math.random() * 70) + 80;
          usageData.dates[dateStr] = dayNum % 2 === 0 ? 25 : 15;
        } else if (item.startsWith("D")) {
          if (dayNum % 6 === 0)
            inputData.dates[dateStr] = Math.floor(Math.random() * 60) + 100;
          usageData.dates[dateStr] =
            dayNum % 5 === 0
              ? 50
              : dayNum % 5 === 1
              ? 40
              : dayNum % 5 === 2
              ? 30
              : dayNum % 5 === 3
              ? 20
              : 10;
        }
        if (index === 0) {
          stockData.dates[dateStr] =
            stockData.value +
            (inputData.dates[dateStr] || 0) -
            (usageData.dates[dateStr] || 0);
        } else {
          const prevDate = displayDates[index - 1];
          stockData.dates[dateStr] =
            stockData.dates[prevDate] +
            (inputData.dates[dateStr] || 0) -
            (usageData.dates[dateStr] || 0);
        }
        safetyStockData.dates[dateStr] = safetyStockData.value;
      });
      result.push(inputData, usageData, stockData, safetyStockData);
    });
    return result;
  }, [displayDates, itemCategories]);

  const generateSummaryDataFromInventory = useCallback(
    (inventoryData, selectedItems) => {
      const summaryTypes = [
        "기초재고",
        "출고예정",
        "예상재고",
        "생산계획",
        "입고예정",
        "리소스 현황",
      ];
      const result = [];
      const filteredInventory = inventoryData.filter(
        (item) => !item.itemCode || selectedItems.includes(item.itemCode)
      );
      summaryTypes.forEach((type) => {
        const summaryItem = {
          id: `summary-${type}`,
          type: type,
          value: 0,
          dates: {},
        };
        if (type === "기초재고") {
          const firstDate = displayDates[0];
          let totalInitialStock = 0;
          filteredInventory.forEach((item) => {
            if (
              item.type === "재고량" &&
              !item.isGroupHeader &&
              item.dates[firstDate] &&
              selectedItems.includes(item.itemCode)
            ) {
              totalInitialStock += item.dates[firstDate];
              if (!summaryItem.dates[firstDate])
                summaryItem.dates[firstDate] = 0;
              summaryItem.dates[firstDate] += item.dates[firstDate];
            }
          });
          summaryItem.value = totalInitialStock;
        } else if (type === "출고예정") {
          let totalOutgoing = 0;
          displayDates.forEach((date, index) => {
            if (index % 4 === 0 || index % 7 === 0) {
              let outgoingValue = 0;
              filteredInventory.forEach((item) => {
                if (
                  item.type === "사용량" &&
                  !item.isGroupHeader &&
                  item.dates[date] &&
                  selectedItems.includes(item.itemCode)
                ) {
                  outgoingValue += Math.round(item.dates[date] * 1.5);
                }
              });

              if (outgoingValue > 0) {
                summaryItem.dates[date] = outgoingValue;
                totalOutgoing += outgoingValue;
              }
            }
          });
          summaryItem.value = totalOutgoing;
        } else if (type === "예상재고") {
          let totalEstimatedStock = 0;
          displayDates.forEach((date) => {
            summaryItem.dates[date] = 0;
          });
          filteredInventory.forEach((item) => {
            if (
              item.type === "재고량" &&
              !item.isGroupHeader &&
              selectedItems.includes(item.itemCode)
            ) {
              Object.entries(item.dates).forEach(([date, value]) => {
                summaryItem.dates[date] += value;
              });
              if (item.value) {
                totalEstimatedStock += item.value;
              }
            }
          });
          summaryItem.value = totalEstimatedStock;
        } else if (type === "생산계획") {
          let totalProduction = 0;

          displayDates.forEach((date, index) => {
            const dayNum = parseInt(date.substring(4, 6));

            // 일부 날짜에만 생산 계획 설정
            if (dayNum % 2 === 0 || dayNum % 5 === 0) {
              const value =
                dayNum % 2 === 0 && dayNum % 5 === 0
                  ? 120
                  : dayNum % 5 === 0
                  ? 80
                  : 40;
              summaryItem.dates[date] = value;
              totalProduction += value;
            }
          });

          summaryItem.value = totalProduction;
        } else if (type === "입고예정") {
          let totalIncoming = 0;
          filteredInventory.forEach((item) => {
            if (
              item.type === "입고량" &&
              !item.isGroupHeader &&
              selectedItems.includes(item.itemCode)
            ) {
              Object.entries(item.dates).forEach(([date, value]) => {
                if (!summaryItem.dates[date]) summaryItem.dates[date] = 0;
                summaryItem.dates[date] += value;
                totalIncoming += value;
              });
            }
          });

          // 몇몇 날짜에 추가 입고 예정 설정
          displayDates.forEach((date, index) => {
            if (index % 5 === 0 && !summaryItem.dates[date]) {
              const value = 30;
              summaryItem.dates[date] = value;
              totalIncoming += value;
            }
          });

          summaryItem.value = totalIncoming;
        } else if (type === "리소스 현황") {
          summaryItem.value = null;
        }

        result.push(summaryItem);
      });

      return result;
    },
    [displayDates]
  );

  // FullCalendar용 이벤트 데이터 생성 함수
  const generateCalendarEvents = useCallback((summaryData) => {
    if (!summaryData.length) return [];

    const events = [];

    // 생산계획 이벤트
    const productionPlan = summaryData.find((item) => item.type === "생산계획");
    if (productionPlan) {
      Object.entries(productionPlan.dates).forEach(([dateStr, value]) => {
        // "09월 15일" 형식을 "2023-09-15" 형식으로 변환
        const month = dateStr.substring(0, 2);
        const day = dateStr.substring(4, 6);
        const dateISOStr = `2023-${month}-${day}`;

        events.push({
          id: `production-${dateISOStr}`,
          title: `생산: ${value}`,
          start: dateISOStr,
          classNames: ["production-event"],
          extendedProps: {
            type: "production",
            value: value,
          },
        });
      });
    }

    // 입고예정 이벤트
    const inputPlan = summaryData.find((item) => item.type === "입고예정");
    if (inputPlan) {
      Object.entries(inputPlan.dates).forEach(([dateStr, value]) => {
        const month = dateStr.substring(0, 2);
        const day = dateStr.substring(4, 6);
        const dateISOStr = `2023-${month}-${day}`;

        events.push({
          id: `input-${dateISOStr}`,
          title: `입고: ${value}`,
          start: dateISOStr,
          classNames: ["input-event"],
          extendedProps: {
            type: "input",
            value: value,
          },
        });
      });
    }

    // 출고예정 이벤트
    const outputPlan = summaryData.find((item) => item.type === "출고예정");
    if (outputPlan) {
      Object.entries(outputPlan.dates).forEach(([dateStr, value]) => {
        const month = dateStr.substring(0, 2);
        const day = dateStr.substring(4, 6);
        const dateISOStr = `2023-${month}-${day}`;

        events.push({
          id: `output-${dateISOStr}`,
          title: `출고: ${value}`,
          start: dateISOStr,
          classNames: ["output-event"],
          extendedProps: {
            type: "output",
            value: value,
          },
        });
      });
    }

    return events;
  }, []);

  const generateChartOption = useCallback(
    (inventoryData, selectedItems, chartDateLabels) => {
      if (!inventoryData || !inventoryData.length) return null;

      const series = [];
      const itemColors = {
        A: ["#1890ff", "#91d5ff", "#69c0ff", "#bae7ff"],
        B: ["#52c41a", "#b7eb8f", "#95de64", "#d9f7be"],
        C: ["#fa8c16", "#ffd591", "#ffa940", "#ffe7ba"],
        D: ["#722ed1", "#d3adf7", "#9254de", "#efdbff"],
      };

      // 선택된 아이템 중 데이터가 있는지 확인
      let hasData = false;

      selectedItems.forEach((itemCode) => {
        // 아이템 색상 결정 (첫 글자로 카테고리 구분)
        const colorCategory = itemCode.charAt(0);

        const inputData = inventoryData.find(
          (item) =>
            item.type === "입고량" &&
            item.itemCode === itemCode &&
            !item.isGroupHeader
        );

        if (inputData) {
          const data = chartDateLabels.map((date) => {
            const fullDate = date.replace("/", "월 ").concat("일");
            return inputData.dates[fullDate] || 0;
          });

          // 값이 있는지 확인
          if (data.some((value) => value > 0)) {
            hasData = true;
          }

          series.push({
            name: `아이템 ${itemCode} - 입고량`,
            type: "line",
            data: data,
            symbol: "circle",
            symbolSize: 8,
            lineStyle: {
              width: 2,
              type: "solid",
            },
            itemStyle: {
              color: itemColors[colorCategory][0],
            },
          });
        }

        const stockData = inventoryData.find(
          (item) =>
            item.type === "재고량" &&
            item.itemCode === itemCode &&
            !item.isGroupHeader
        );

        if (stockData) {
          const data = chartDateLabels.map((date) => {
            const fullDate = date.replace("/", "월 ").concat("일");
            return stockData.dates[fullDate] || 0;
          });

          // 값이 있는지 확인
          if (data.some((value) => value > 0)) {
            hasData = true;
          }

          series.push({
            name: `아이템 ${itemCode} - 재고량`,
            type: "line",
            data: data,
            symbol: "rect",
            symbolSize: 8,
            lineStyle: {
              width: 2,
              type: "solid",
            },
            itemStyle: {
              color: itemColors[colorCategory][1],
            },
          });
        }

        const usageData = inventoryData.find(
          (item) =>
            item.type === "사용량" &&
            item.itemCode === itemCode &&
            !item.isGroupHeader
        );

        if (usageData) {
          const data = chartDateLabels.map((date) => {
            const fullDate = date.replace("/", "월 ").concat("일");
            return usageData.dates[fullDate] || 0;
          });

          // 값이 있는지 확인
          if (data.some((value) => value > 0)) {
            hasData = true;
          }

          series.push({
            name: `아이템 ${itemCode} - 사용량`,
            type: "line",
            data: data,
            symbol: "triangle",
            symbolSize: 8,
            lineStyle: {
              width: 2,
              type: "solid",
            },
            itemStyle: {
              color: itemColors[colorCategory][2],
            },
          });
        }

        const safetyData = inventoryData.find(
          (item) =>
            item.type === "안전재고" &&
            item.itemCode === itemCode &&
            !item.isGroupHeader
        );

        if (safetyData) {
          const safetyValue = safetyData.value || 30;

          series.push({
            name: `아이템 ${itemCode} - 안전재고`,
            type: "line",
            data: Array(chartDateLabels.length).fill(safetyValue),
            symbol: "none",
            lineStyle: {
              width: 2,
              type: "dashed",
            },
            itemStyle: {
              color: itemColors[colorCategory][3],
            },
          });
        }
      });

      // 차트 데이터가 없는 경우 기본 시리즈 추가
      if (!hasData) {
        series.push({
          name: "데이터 없음",
          type: "line",
          data: Array(chartDateLabels.length).fill(0),
          lineStyle: {
            width: 1,
            type: "dashed",
            color: "#ccc",
          },
        });
      }

      return {
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "cross",
            label: {
              backgroundColor: "#6a7985",
            },
          },
        },
        legend: {
          data: series.map((s) => s.name),
          bottom: 0,
          type: "scroll",
          pageButtonPosition: "end",
          padding: [5, 40, 5, 40],
          selector: ["all", "inverse"],
        },
        grid: {
          left: "3%",
          right: "4%",
          bottom: "80px",
          top: "30px",
          containLabel: true,
        },
        dataZoom: [
          {
            type: "inside",
            start: 0,
            end: 100,
          },
          {
            type: "slider",
            start: 0,
            end: 100,
            height: 20,
            bottom: 60,
          },
        ],
        xAxis: {
          type: "category",
          boundaryGap: false,
          data: chartDateLabels,
        },
        yAxis: {
          type: "value",
          min: 0,
          minInterval: 1,
        },
        series: series,
      };
    },
    []
  );

  const mockInventoryData = useMemo(
    () => generateHierarchicalInventoryData(),
    [generateHierarchicalInventoryData]
  );

  const mockSummaryData = useMemo(
    () => generateSummaryDataFromInventory(mockInventoryData, selectedItems),
    [mockInventoryData, selectedItems, generateSummaryDataFromInventory]
  );

  // FullCalendar 이벤트 데이터 생성
  useEffect(() => {
    if (mockSummaryData.length) {
      setCalendarEvents(generateCalendarEvents(mockSummaryData));
    }
  }, [mockSummaryData, generateCalendarEvents]);

  const currentChartOption = useMemo(() => {
    return generateChartOption(
      mockInventoryData,
      selectedItems,
      chartDateLabels
    );
  }, [mockInventoryData, selectedItems, chartDateLabels, generateChartOption]);

  // 차트 데이터가 변경될 때마다 차트 업데이트
  useEffect(() => {
    if (chartInstance) {
      const newOption = generateChartOption(
        mockInventoryData,
        selectedItems,
        chartDateLabels
      );
      // 차트 옵션이 null이 아닌 경우에만 설정
      if (newOption) {
        chartInstance.setOption(newOption, true);

        // 데이터 변경 후 차트 리사이즈 트리거
        setTimeout(() => {
          chartInstance.resize();
        }, 300);
      }
    }
  }, [
    selectedItems,
    chartInstance,
    mockInventoryData,
    chartDateLabels,
    generateChartOption,
  ]);

  // 창 크기가 변경될 때 차트 리사이즈
  useEffect(() => {
    const handleResize = () => {
      if (chartInstance) {
        chartInstance.resize();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [chartInstance]);

  useEffect(() => {
    async function fetchData() {
      try {
        // 실제 API 호출
        // const response = await axios.get(`${PLAN_URL}/api/result-grid`);
        // const inventoryData = response.data.inventory || generateHierarchicalInventoryData();
        // setSummaryData(generateSummaryDataFromInventory(inventoryData, selectedItems));

        setSummaryData(mockSummaryData);
        setLoading(false);
      } catch (e) {
        console.log(e);
        setLoading(false);
      }
    }
    fetchData();
  }, [mockSummaryData]);

  // 선택된 아이템이 변경될 때 summaryData도 업데이트
  useEffect(() => {
    if (!loading) {
      setSummaryData(
        generateSummaryDataFromInventory(mockInventoryData, selectedItems)
      );
    }
  }, [
    selectedItems,
    mockInventoryData,
    loading,
    generateSummaryDataFromInventory,
  ]);

  // 캘린더 뷰에서 날짜 범위가 변경되었을 때 동작
  const handleCalendarDateSet = (dateInfo) => {
    if (calendarRef.current && viewMode === "calendar") {
      // FullCalendar의 날짜 범위를 기반으로 dateRange 업데이트
      setDateRange({
        start: dayjs(dateInfo.start),
        end: dayjs(dateInfo.end),
      });
      setPresetPeriod("custom");
    }
  };

  // 이벤트 클릭 핸들러
  const handleEventClick = (clickInfo) => {
    const eventType = clickInfo.event.extendedProps.type;
    const value = clickInfo.event.extendedProps.value;
    const dateStr = dayjs(clickInfo.event.startStr).format("YYYY년 MM월 DD일");

    let typeText = "";
    switch (eventType) {
      case "production":
        typeText = "생산계획";
        break;
      case "input":
        typeText = "입고예정";
        break;
      case "output":
        typeText = "출고예정";
        break;
      default:
        typeText = "일정";
    }

    alert(`${dateStr} ${typeText}: ${value}`);
  };

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);

      // 캘린더 뷰로 전환 시 필요한 경우 캘린더 API 호출
      if (newMode === "calendar" && calendarRef.current) {
        calendarRef.current.getApi().render();
      }

      // 차트 뷰로 전환 시 차트 리사이즈
      if (newMode === "table" && chartInstance) {
        setTimeout(() => {
          chartInstance.resize();
        }, 300);
      }
    }
  };

  // 기간 프리셋 변경 처리
  const handlePresetChange = (preset) => {
    setPresetPeriod(preset);

    const today = dayjs();
    let newStart, newEnd;

    switch (preset) {
      case "1week":
        newStart = today.subtract(7, "day");
        newEnd = today;
        break;
      case "2weeks":
        newStart = today.subtract(14, "day");
        newEnd = today;
        break;
      case "1month":
        newStart = today.startOf("month");
        newEnd = today.endOf("month");
        break;
      case "3months":
        newStart = today.subtract(3, "month").startOf("month");
        newEnd = today.endOf("month");
        break;
      default:
        return; // custom은 날짜 선택기에서 직접 설정
    }

    // 임시 날짜 범위 업데이트
    setTempDateRange({
      start: newStart,
      end: newEnd,
    });
  };

  // 날짜 선택 변경 핸들러 수정 - 임시 저장만 하도록
  const handleStartDateChange = (newDate) => {
    setPresetPeriod("custom");
    setTempDateRange((prev) => ({
      ...prev,
      start: newDate,
    }));
  };

  const handleEndDateChange = (newDate) => {
    setPresetPeriod("custom");
    setTempDateRange((prev) => ({
      ...prev,
      end: newDate,
    }));
  };

  // 날짜 범위 적용하기
  const applyDateRange = () => {
    // 임시 날짜 범위를 실제 날짜 범위로 적용
    setDateRange(tempDateRange);
    setIsDatePanelOpen(false);

    // 캘린더 API를 통해 해당 날짜로 이동
    if (calendarRef.current && viewMode === "calendar") {
      calendarRef.current.getApi().gotoDate(tempDateRange.start.toDate());
    }
  };

  // 날짜 범위 변경 취소
  const cancelDateRange = () => {
    // 임시 날짜 범위를 현재 날짜 범위로 초기화
    setTempDateRange(dateRange);
    setIsDatePanelOpen(false);
  };

  const summaryColumns = useMemo(() => {
    const cols = [
      {
        accessorKey: "type",
        header: "항목",
        size: 120,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        muiTableFooterCellProps: { align: "center" },
      },
      {
        accessorKey: "value",
        header: "합계",
        size: 80,
        Cell: ({ cell }) => cell.getValue() || "",
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        muiTableFooterCellProps: { align: "center" },
      },
    ];

    displayDates.forEach((date) => {
      cols.push({
        accessorFn: (row) =>
          row.dates && row.dates[date] !== undefined ? row.dates[date] : "",
        id: date,
        header: date,
        size: 100,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        muiTableFooterCellProps: { align: "center" },

        Cell: ({ cell, row }) => {
          const value = cell.getValue();
          if (value === "") return "";
          if (row.original.type === "예상재고") {
            if (value === 0)
              return <Box sx={{ bgcolor: "error.light", p: 1 }}>{value}</Box>;
            if (value <= 30)
              return <Box sx={{ bgcolor: "warning.light", p: 1 }}>{value}</Box>;
            if (value <= 60)
              return <Box sx={{ bgcolor: "warning.main", p: 1 }}>{value}</Box>;
            if (value <= 90)
              return <Box sx={{ bgcolor: "warning.dark", p: 1 }}>{value}</Box>;
          }
          if (row.original.type === "입고예정") {
            if (value === 30)
              return <Box sx={{ bgcolor: "orange.light", p: 1 }}>{value}</Box>;
          }
          return value;
        },
      });
    });
    return cols;
  }, [displayDates]);

  // 카테고리 확장/축소 토글
  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  // 아이템 선택/해제 핸들러
  const handleToggleItem = (itemCode) => {
    setDataLoading(true);

    setTimeout(() => {
      if (selectedItems.includes(itemCode)) {
        if (selectedItems.length > 1) {
          // 최소 하나는 선택되어 있어야 함
          setSelectedItems(selectedItems.filter((item) => item !== itemCode));
        }
      } else {
        setSelectedItems([...selectedItems, itemCode]);
      }
      setDataLoading(false);
    }, 300);
  };

  // 카테고리 내 모든 아이템 선택/해제
  const handleSelectAllInCategory = (category, isSelected) => {
    setDataLoading(true);

    setTimeout(() => {
      const categoryItems = itemCategories[category];

      if (isSelected) {
        // 카테고리 내 아이템 모두 선택 해제
        const newSelected = selectedItems.filter(
          (item) => !categoryItems.includes(item)
        );
        if (newSelected.length > 0) {
          // 최소 하나는 선택되어 있어야 함
          setSelectedItems(newSelected);
        }
      } else {
        // 카테고리 내 아이템 모두 선택
        const itemsToAdd = categoryItems.filter(
          (item) => !selectedItems.includes(item)
        );
        setSelectedItems([...selectedItems, ...itemsToAdd]);
      }

      setDataLoading(false);
    }, 300);
  };

  // 검색어에 따른 필터링된 카테고리와 아이템 목록
  const filteredCategories = useMemo(() => {
    if (!searchTerm) return Object.keys(itemCategories);

    return Object.keys(itemCategories).filter(
      (category) =>
        category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        itemCategories[category].some(
          (item) =>
            item.toLowerCase().includes(searchTerm.toLowerCase()) ||
            `${category} ${item}`
              .toLowerCase()
              .includes(searchTerm.toLowerCase())
        )
    );
  }, [itemCategories, searchTerm]);

  // 검색어에 따른 필터링된 아이템 목록
  const getFilteredItemsInCategory = useCallback(
    (category) => {
      if (!searchTerm) return itemCategories[category];

      return itemCategories[category].filter(
        (item) =>
          item.toLowerCase().includes(searchTerm.toLowerCase()) ||
          `${category} ${item}`.toLowerCase().includes(searchTerm.toLowerCase())
      );
    },
    [itemCategories, searchTerm]
  );

  // 모든 선택 해제
  const clearAllSelections = () => {
    setDataLoading(true);
    setTimeout(() => {
      // 최소 하나는 유지
      setSelectedItems([Object.values(itemCategories)[0][0]]);
      setDataLoading(false);
    }, 300);
  };

  // 검색어 초기화
  const clearSearch = () => {
    setSearchTerm("");
  };

  // 패널 토글 함수
  const toggleItemPanel = () => {
    // 다른 패널이 열려있으면 닫기
    if (isDatePanelOpen) {
      setIsDatePanelOpen(false);
    }
    setIsItemPanelOpen(!isItemPanelOpen);
  };

  const toggleDatePanel = () => {
    // 다른 패널이 열려있으면 닫기
    if (isItemPanelOpen) {
      setIsItemPanelOpen(false);
    }
    // 패널 열릴 때 현재 날짜 범위로 임시 날짜 범위 초기화
    if (!isDatePanelOpen) {
      setTempDateRange(dateRange);
    }
    setIsDatePanelOpen(!isDatePanelOpen);
  };

  // 패널 외부 클릭 핸들러
  useEffect(() => {
    const handleClickOutside = (event) => {
      // 아이템 패널 외부 클릭 처리
      if (
        isItemPanelOpen &&
        mainContentRef.current &&
        !event.target.closest(".item-panel") &&
        !event.target.closest(".item-panel-toggle")
      ) {
        setIsItemPanelOpen(false);
      }

      // 날짜 패널은 외부 클릭으로 닫지 않음 (버튼 클릭으로만 닫히도록)
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isItemPanelOpen]);

  // 패널이 열릴 때 임시 날짜 범위 초기화
  useEffect(() => {
    if (isDatePanelOpen) {
      setTempDateRange(dateRange);
    }
  }, [isDatePanelOpen, dateRange]);

  // 차트 리사이즈 트리거 - 컴포넌트 마운트 시와 차트 데이터 변경 시
  useEffect(() => {
    if (chartInstance && viewMode === "table") {
      setTimeout(() => {
        chartInstance.resize();
      }, 500);
    }
  }, [chartInstance, viewMode, dataLoading]);

  return (
    <Wrapper>
      <FrameContainer>
        <ToolbarContainer>
          <ToolbarLeft>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ fontSize: "22px !important", color: "#111" }}
            >
              계획 결과
            </Typography>
          </ToolbarLeft>
          <ToolbarRight>
            <DateRangeDisplay
              onClick={toggleDatePanel}
              className="date-panel-toggle"
            >
              <DateRangeIcon fontSize="small" />
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{ color: "white" }}
              >
                {dateRange.start.format("MM/DD")} ~{" "}
                {dateRange.end.format("MM/DD")}
              </Typography>
            </DateRangeDisplay>

            <Badge
              badgeContent={selectedItems.length}
              color="primary"
              overlap="circular"
            >
              <ToolbarIconButton
                onClick={toggleItemPanel}
                title="아이템 선택"
                $active={isItemPanelOpen}
                className="item-panel-toggle"
              >
                <FilterListIcon />
              </ToolbarIconButton>
            </Badge>

            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
              sx={{ mx: 1 }}
            >
              <ToggleButton value="table" aria-label="표 보기">
                <TableChartIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="calendar" aria-label="달력 보기">
                <CalendarMonthIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>

            <Tooltip title="도움말">
              <IconButton size="small">
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </ToolbarRight>
        </ToolbarContainer>

        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <CircularProgress />
          </Box>
        ) : (
          <ContentContainer>
            {/* 아이템 선택 패널 - 오른쪽에서 슬라이드 */}
            <ItemSelectionPanel
              $isOpen={isItemPanelOpen}
              className="item-panel"
            >
              <PanelHeader>
                <Typography variant="subtitle1" fontWeight="bold">
                  아이템 선택
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={clearAllSelections}
                    title="모든 선택 해제"
                  >
                    <RestartAltIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={toggleItemPanel}
                    title="닫기"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </PanelHeader>

              <Box sx={{ p: 2, pb: 1 }}>
                <TextField
                  placeholder="아이템 검색..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={clearSearch}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <PanelContent>
                <Typography variant="subtitle2" gutterBottom fontWeight={500}>
                  선택된 아이템 ({selectedItems.length})
                </Typography>
                <SelectedItemsContainer>
                  {selectedItems.length === 0 ? (
                    <Typography color="text.secondary">
                      선택된 아이템이 없습니다.
                    </Typography>
                  ) : (
                    selectedItems.map((item) => (
                      <StyledChip
                        key={item}
                        label={item}
                        color="primary"
                        onDelete={() => handleToggleItem(item)}
                        sx={{ fontWeight: 500 }}
                      />
                    ))
                  )}
                </SelectedItemsContainer>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom fontWeight={500}>
                  아이템 목록
                </Typography>

                {filteredCategories.map((category) => {
                  const filteredItems = getFilteredItemsInCategory(category);
                  const categorySelected = filteredItems.every((item) =>
                    selectedItems.includes(item)
                  );
                  const someSelected = filteredItems.some((item) =>
                    selectedItems.includes(item)
                  );

                  return (
                    <CategoryContainer key={category}>
                      <CategoryHeader>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flex: 1,
                            cursor: "pointer",
                          }}
                          onClick={() => toggleCategory(category)}
                        >
                          {expandedCategories[category] ? (
                            <KeyboardArrowDownIcon fontSize="small" />
                          ) : (
                            <KeyboardArrowRightIcon fontSize="small" />
                          )}
                          <Typography
                            variant="body2"
                            sx={{ ml: 1, fontWeight: "medium" }}
                          >
                            {category} ({filteredItems.length})
                          </Typography>
                        </Box>

                        <IconButton
                          size="small"
                          onClick={() =>
                            handleSelectAllInCategory(
                              category,
                              categorySelected
                            )
                          }
                          sx={{ mr: -1 }}
                        >
                          {categorySelected ? (
                            <Checkbox size="small" checked />
                          ) : someSelected ? (
                            <Checkbox size="small" indeterminate />
                          ) : (
                            <Checkbox size="small" />
                          )}
                        </IconButton>
                      </CategoryHeader>

                      <Collapse in={expandedCategories[category]}>
                        <ItemList>
                          {filteredItems.map((item) => (
                            <StyledChip
                              key={item}
                              label={item}
                              size="small"
                              variant={
                                selectedItems.includes(item)
                                  ? "filled"
                                  : "outlined"
                              }
                              color={
                                selectedItems.includes(item)
                                  ? "primary"
                                  : "default"
                              }
                              onClick={() => handleToggleItem(item)}
                              sx={{
                                m: 0.5,
                                fontWeight: selectedItems.includes(item)
                                  ? 500
                                  : 400,
                              }}
                            />
                          ))}
                        </ItemList>
                      </Collapse>
                    </CategoryContainer>
                  );
                })}

                {filteredCategories.length === 0 && (
                  <Typography
                    color="text.secondary"
                    sx={{ py: 2, textAlign: "center" }}
                  >
                    검색 결과가 없습니다.
                  </Typography>
                )}
              </PanelContent>
            </ItemSelectionPanel>

            {/* 기간 설정 패널 - 위에서 슬라이드 */}
            <DateSelectionPanel
              $isOpen={isDatePanelOpen}
              className="date-panel"
            >
              <PanelHeader>
                <Typography variant="subtitle1" fontWeight="bold">
                  기간 설정
                </Typography>
                <IconButton size="small" onClick={cancelDateRange} title="닫기">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </PanelHeader>

              <DatePickerContainer>
                <Typography variant="subtitle2" fontWeight={500} gutterBottom>
                  기간 빠른 선택
                </Typography>

                <PresetButtonGroup>
                  <PresetButton
                    variant={
                      presetPeriod === "1week" ? "contained" : "outlined"
                    }
                    color="primary"
                    size="small"
                    onClick={() => handlePresetChange("1week")}
                  >
                    최근 1주
                  </PresetButton>
                  <PresetButton
                    variant={
                      presetPeriod === "2weeks" ? "contained" : "outlined"
                    }
                    color="primary"
                    size="small"
                    onClick={() => handlePresetChange("2weeks")}
                  >
                    최근 2주
                  </PresetButton>
                  <PresetButton
                    variant={
                      presetPeriod === "1month" ? "contained" : "outlined"
                    }
                    color="primary"
                    size="small"
                    onClick={() => handlePresetChange("1month")}
                  >
                    이번 달
                  </PresetButton>
                  <PresetButton
                    variant={
                      presetPeriod === "3months" ? "contained" : "outlined"
                    }
                    color="primary"
                    size="small"
                    onClick={() => handlePresetChange("3months")}
                  >
                    최근 3개월
                  </PresetButton>
                </PresetButtonGroup>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" fontWeight={500} gutterBottom>
                  직접 선택
                </Typography>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePickerWrapper>
                    <DatePicker
                      label="시작일"
                      value={tempDateRange.start}
                      onChange={handleStartDateChange}
                      slotProps={{
                        textField: { size: "small", fullWidth: true },
                      }}
                    />
                    <DatePicker
                      label="종료일"
                      value={tempDateRange.end}
                      onChange={handleEndDateChange}
                      slotProps={{
                        textField: { size: "small", fullWidth: true },
                      }}
                    />
                  </DatePickerWrapper>
                </LocalizationProvider>

                <ButtonGroup>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={cancelDateRange}
                  >
                    취소
                  </Button>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={applyDateRange}
                    startIcon={<DateRangeIcon />}
                  >
                    적용하기
                  </Button>
                </ButtonGroup>
              </DatePickerContainer>
            </DateSelectionPanel>
            <MainContent ref={mainContentRef}>
              {viewMode === "table" && (
                <>
                  <Fade in={!dataLoading} timeout={500}>
                    <TableContainer>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{ px: 2, pt: 2 }}
                      >
                        요약
                      </Typography>
                      {dataLoading ? (
                        <Box sx={{ p: 2 }}>
                          <Skeleton
                            variant="rectangular"
                            width="100%"
                            height={200}
                            animation="wave"
                          />
                        </Box>
                      ) : (
                        <MaterialReactTable
                          columns={summaryColumns}
                          data={summaryData}
                          enableTopToolbar={false}
                          enableColumnActions={false}
                          enableColumnPinning={true}
                          enableColumnFilters={false}
                          enablePagination={false}
                          enableSorting={false}
                          enableBottomToolbar={false}
                          enableRowSelection={false}
                          initialState={{ columnPinning: { left: ["type"] } }}
                          muiTableProps={{
                            sx: {
                              tableLayout: "fixed",
                            },
                          }}
                          muiTableHeadCellProps={{
                            sx: {
                              textAlign: "center",
                              backgroundColor: "#f5f5f5",
                            },
                          }}
                          muiTableHeadRowProps={{
                            sx: {
                              "& > th": {
                                borderRight: "1px solid #E0DEE3",
                              },
                            },
                          }}
                          muiTableBodyRowProps={{
                            sx: {
                              height: "40px !important",
                              maxHeight: "40px !important",
                              "& > td": {
                                height: "40px !important",
                                padding: "0px 16px",
                                borderRight: "1px solid #E0DEE3",
                              },
                              "&:hover": {
                                backgroundColor: "#f5f5f5 !important",
                              },
                            },
                          }}
                          muiTableBodyCellProps={{
                            sx: {
                              height: "40px !important",
                              padding: "0px 16px !important",
                              verticalAlign: "middle",
                              textAlign: "center",
                            },
                          }}
                          muiTableContainerProps={{
                            sx: {
                              maxHeight: "300px",
                              "&::-webkit-scrollbar": {
                                width: "8px",
                                height: "8px",
                              },
                              "&::-webkit-scrollbar-thumb": {
                                backgroundColor: "rgba(99, 96, 150, 0.7)",
                                borderRadius: "5px",
                              },
                              "&::-webkit-scrollbar-track": {
                                backgroundColor: "transparent",
                              },
                            },
                          }}
                        />
                      )}
                    </TableContainer>
                  </Fade>

                  {/* 차트 */}
                  <Fade in={!dataLoading} timeout={700}>
                    <ChartContainer>
                      <Typography variant="h6" gutterBottom>
                        아이템 분석
                      </Typography>
                      {dataLoading ? (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Skeleton
                            variant="rectangular"
                            width="100%"
                            height="100%"
                            animation="wave"
                          />
                        </Box>
                      ) : (
                        // 차트 컴포넌트 부분
                        <ReactEcharts
                          ref={chartRef}
                          option={{
                            tooltip: {
                              trigger: "axis",
                              axisPointer: {
                                type: "cross",
                                label: {
                                  backgroundColor: "#6a7985",
                                },
                              },
                            },
                            legend: {
                              data: selectedItems
                                .map((item) => [
                                  `아이템 ${item} - 입고량`,
                                  `아이템 ${item} - 재고량`,
                                  `아이템 ${item} - 사용량`,
                                  `아이템 ${item} - 안전재고`,
                                ])
                                .flat(),
                              bottom: 0,
                              type: "scroll",
                              pageButtonPosition: "end",
                              padding: [5, 40, 5, 40],
                              selector: ["all", "inverse"],
                            },
                            grid: {
                              left: "3%",
                              right: "4%",
                              bottom: "80px",
                              top: "30px",
                              containLabel: true,
                            },
                            dataZoom: [
                              {
                                type: "inside",
                                start: 0,
                                end: 100,
                              },
                              {
                                type: "slider",
                                start: 0,
                                end: 100,
                                height: 20,
                                bottom: 60,
                              },
                            ],
                            xAxis: {
                              type: "category",
                              boundaryGap: false,
                              data: chartDateLabels,
                            },
                            yAxis: {
                              type: "value",
                              min: 0,
                              minInterval: 1,
                            },
                            series: selectedItems.flatMap((itemCode) => {
                              const colorCategory = itemCode.charAt(0);
                              const itemColors = {
                                A: ["#1890ff", "#91d5ff", "#69c0ff", "#bae7ff"],
                                B: ["#52c41a", "#b7eb8f", "#95de64", "#d9f7be"],
                                C: ["#fa8c16", "#ffd591", "#ffa940", "#ffe7ba"],
                                D: ["#722ed1", "#d3adf7", "#9254de", "#efdbff"],
                              };

                              // 입고량 데이터
                              const inputSeries = {
                                name: `아이템 ${itemCode} - 입고량`,
                                type: "line",
                                data: chartDateLabels.map((date) => {
                                  const fullDate = date
                                    .replace("/", "월 ")
                                    .concat("일");
                                  const item = mockInventoryData.find(
                                    (item) =>
                                      item.type === "입고량" &&
                                      item.itemCode === itemCode &&
                                      !item.isGroupHeader
                                  );
                                  return item && item.dates[fullDate]
                                    ? item.dates[fullDate]
                                    : 0;
                                }),
                                symbol: "circle",
                                symbolSize: 8,
                                lineStyle: {
                                  width: 2,
                                  type: "solid",
                                },
                                itemStyle: {
                                  color: itemColors[colorCategory][0],
                                },
                              };

                              // 재고량 데이터
                              const stockSeries = {
                                name: `아이템 ${itemCode} - 재고량`,
                                type: "line",
                                data: chartDateLabels.map((date) => {
                                  const fullDate = date
                                    .replace("/", "월 ")
                                    .concat("일");
                                  const item = mockInventoryData.find(
                                    (item) =>
                                      item.type === "재고량" &&
                                      item.itemCode === itemCode &&
                                      !item.isGroupHeader
                                  );
                                  return item && item.dates[fullDate]
                                    ? item.dates[fullDate]
                                    : 0;
                                }),
                                symbol: "rect",
                                symbolSize: 8,
                                lineStyle: {
                                  width: 2,
                                  type: "solid",
                                },
                                itemStyle: {
                                  color: itemColors[colorCategory][1],
                                },
                              };

                              // 사용량 데이터
                              const usageSeries = {
                                name: `아이템 ${itemCode} - 사용량`,
                                type: "line",
                                data: chartDateLabels.map((date) => {
                                  const fullDate = date
                                    .replace("/", "월 ")
                                    .concat("일");
                                  const item = mockInventoryData.find(
                                    (item) =>
                                      item.type === "사용량" &&
                                      item.itemCode === itemCode &&
                                      !item.isGroupHeader
                                  );
                                  return item && item.dates[fullDate]
                                    ? item.dates[fullDate]
                                    : 0;
                                }),
                                symbol: "triangle",
                                symbolSize: 8,
                                lineStyle: {
                                  width: 2,
                                  type: "solid",
                                },
                                itemStyle: {
                                  color: itemColors[colorCategory][2],
                                },
                              };

                              // 안전재고 데이터
                              const safetyStockSeries = {
                                name: `아이템 ${itemCode} - 안전재고`,
                                type: "line",
                                data: Array(chartDateLabels.length).fill(30),
                                symbol: "none",
                                lineStyle: {
                                  width: 2,
                                  type: "dashed",
                                },
                                itemStyle: {
                                  color: itemColors[colorCategory][3],
                                },
                              };

                              return [
                                inputSeries,
                                stockSeries,
                                usageSeries,
                                safetyStockSeries,
                              ];
                            }),
                          }}
                          style={{
                            height: "calc(100% - 40px)",
                            width: "100%",
                          }}
                          opts={{
                            renderer: "canvas",
                            width: "auto",
                            height: "auto",
                          }}
                          onChartReady={onChartReady}
                          theme="light"
                          notMerge={false}
                          lazyUpdate={false}
                        />
                      )}
                    </ChartContainer>
                  </Fade>
                </>
              )}
              {viewMode === "calendar" && (
                <Fade in={!dataLoading} timeout={500}>
                  <CalendarContainer>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Typography variant="h6">생산계획 달력</Typography>
                      <Tooltip title="상세 분석">
                        <IconButton size="small">
                          <AssessmentIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <LegendContainer>
                      <LegendItem>
                        <LegendColor color="#1890ff" />
                        <Typography variant="body2">생산계획</Typography>
                      </LegendItem>
                      <LegendItem>
                        <LegendColor color="#52c41a" />
                        <Typography variant="body2">입고예정</Typography>
                      </LegendItem>
                      <LegendItem>
                        <LegendColor color="#fa8c16" />
                        <Typography variant="body2">출고예정</Typography>
                      </LegendItem>
                    </LegendContainer>
                    {dataLoading ? (
                      <Box sx={{ p: 2, height: "calc(100% - 80px)" }}>
                        <Skeleton
                          variant="rectangular"
                          width="100%"
                          height="100%"
                          animation="wave"
                        />
                      </Box>
                    ) : (
                      <FullCalendar
                        ref={calendarRef}
                        plugins={[
                          dayGridPlugin,
                          timeGridPlugin,
                          interactionPlugin,
                        ]}
                        initialView="dayGridMonth"
                        headerToolbar={{
                          left: "prev,next today",
                          center: "title",
                          right: "dayGridMonth,timeGridWeek",
                        }}
                        events={calendarEvents}
                        eventClick={handleEventClick}
                        datesSet={handleCalendarDateSet}
                        initialDate={dateRange.start.toDate()}
                        locale={koLocaleFC}
                        eventTimeFormat={{
                          hour: "numeric",
                          minute: "2-digit",
                          meridiem: false,
                        }}
                        height="100%"
                      />
                    )}
                  </CalendarContainer>
                </Fade>
              )}
            </MainContent>
          </ContentContainer>
        )}
      </FrameContainer>
    </Wrapper>
  );
}

export default ResultGrid;
